/* eslint-disable no-nested-ternary */
import {
  ButtonLink,
  Column,
  ImageFromCdn,
  ImageType,
  InputWithAddon,
  Loader,
  Row,
  TextWithLink,
  Typography,
} from '@/components'
import { config } from '@/config'
import { Breakpoint, ComponentColors, ThemeColor } from '@/enums'
import { ArrowRight, SearchIcon } from '@/icons'
import { useLocationContext } from '@/providers'
import { Redirect, RedirectTo } from '@/routes/Redirect'
import { paths } from '@/routes/paths'
import { useContractsOverview } from '@/services'
import { Company } from '@/types/company'
import {
  fromEventTarget,
  getFileNameAndExtension,
  makeLocationPathFromCompanies,
  mapWithKey,
  shouldRedirectToDashboard,
  useGoToPath,
  useIsMobile,
} from '@/utils'
import { evolve, isEmpty, map, partition, pluck, prop } from 'ramda'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'
import { CompanyDisplay } from './CompanyDisplay'
import { KPIOverview } from './KPIOverview/Component'
import { filterCompanies } from './helpers'
import {
  CompaniesColumn,
  Container,
  DashboardHeader,
  DashboardMainContent,
  EmptyCompanySearchContainer,
  HeaderAndSearchContainer,
  HeaderText,
  HeadquartersColumn,
  MiddleSectionColumn,
  HeaderLogoWrapper,
  HeaderTextContainer,
  HeaderSection,
  SearchInputContainer,
  StyledPageWidgetContainer,
} from './styles'

export const Dashboard = () => {
  const { user, companies: data = [] } = useOutletContext<{
    user: { name: string }
    companies: Company[]
  }>()
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const { isMobile } = useIsMobile(Breakpoint.md)

  const {
    data: contractsOverviewData,
    isLoading: isContractsOverviewDataLoading,
  } = useContractsOverview()

  const goToCompaniesAndLocations = useGoToPath(paths.companies)
  const goToContractsOverview = useGoToPath(paths.contractsOverview)

  const { clearLocation } = useLocationContext()

  useEffect(() => {
    clearLocation()
  }, [])

  if (isEmpty(data)) return <Redirect to={paths.authRedirect} />

  if (!shouldRedirectToDashboard(data))
    return (
      <Redirect
        to={
          (makeLocationPathFromCompanies(paths)(
            map(evolve({ locations: pluck('locationId') }), data),
          ) as unknown) as RedirectTo
        }
      />
    )
  const [headquarters, companies] = partition(prop('isHeadquarters'), data)

  const headquartersWithLogo = headquarters.find((hq) => !!hq.logoUrl)

  const [
    headquartersLogoUrl,
    headquartersLogoExtension,
  ] = getFileNameAndExtension(
    headquartersWithLogo?.logoUrl || 'surein-logo.webp',
  )

  const defaultBanner = `dashboard-default-banner.webp`

  if (isContractsOverviewDataLoading) return <Loader />

  const filteredCompanies = filterCompanies(companies, search)
  const filteredHeadquarters = filterCompanies(headquarters, search)

  return (
    <>
      <DashboardMainContent>
        <HeaderSection>
          <DashboardHeader
            logoUrl={`${config.cdn}/${
              headquartersWithLogo?.bannerUrl || defaultBanner
            }`}
          >
            <HeaderTextContainer>
              <Row alignItems="center" gap="6px">
                <HeaderLogoWrapper>
                  <ImageFromCdn
                    name={headquartersLogoUrl}
                    type={headquartersLogoExtension as ImageType}
                  />
                </HeaderLogoWrapper>
                <Column>
                  <Typography
                    color={ThemeColor.b0}
                    responsive
                    variant={isMobile ? 'inputLabel' : 'button'}
                  >
                    {t('companyOverview')}
                  </Typography>
                  <Typography
                    bold
                    color={ThemeColor.b0}
                    responsive
                    variant="h4"
                  >
                    {t('welcome_text_dashboard_with_name', {
                      name: user.name,
                    })}
                  </Typography>
                </Column>
              </Row>
            </HeaderTextContainer>
          </DashboardHeader>
        </HeaderSection>
        <Container>
          <MiddleSectionColumn>
            {isMobile && (
              <KPIOverview
                data={contractsOverviewData?.kpis}
                grossPrice={contractsOverviewData?.kpis.totalGrossPrice}
                onDetailsClick={goToContractsOverview}
                isLoading={isContractsOverviewDataLoading}
              />
            )}
            <CompaniesColumn hasHeadquarters={!!headquarters.length}>
              <HeaderAndSearchContainer>
                <HeaderText bold variant="h4">
                  {t('companiesAndLocations')}
                </HeaderText>
                <SearchInputContainer>
                  <InputWithAddon
                    addonBefore={() => <SearchIcon />}
                    onChange={fromEventTarget(setSearch)}
                    placeholder={t('searchByCompanyOrLocation')}
                    value={search}
                  />
                </SearchInputContainer>
              </HeaderAndSearchContainer>
              {!!headquarters.length && (
                <HeadquartersColumn>
                  {mapWithKey(
                    CompanyDisplay,
                    'companyId',
                    filteredHeadquarters,
                  )}
                </HeadquartersColumn>
              )}
              {search &&
              filteredCompanies.length === 0 &&
              filteredHeadquarters.length === 0 ? (
                <EmptyCompanySearch
                  searchMessage={t('noMatchInSearch', { search })}
                />
              ) : (
                mapWithKey(CompanyDisplay, 'companyId', filteredCompanies)
              )}
              <TextWithLink>
                <Trans
                  components={{
                    ButtonLink: (
                      <ButtonLink
                        color={ComponentColors.primary}
                        onClick={goToCompaniesAndLocations}
                      >
                        {t('add_company_or_location_text')}
                      </ButtonLink>
                    ),
                  }}
                  i18nKey={t('add_company_or_location_text')}
                />
              </TextWithLink>
            </CompaniesColumn>
          </MiddleSectionColumn>
          {!isMobile && (
            <StyledPageWidgetContainer>
              <KPIOverview
                data={contractsOverviewData?.kpis}
                grossPrice={contractsOverviewData?.kpis.totalGrossPrice}
                onDetailsClick={goToContractsOverview}
                isLoading={isContractsOverviewDataLoading}
              />
            </StyledPageWidgetContainer>
          )}
        </Container>
      </DashboardMainContent>
    </>
  )
}

interface EmptyCompanySearchProps {
  searchMessage: string
}

const EmptyCompanySearch = ({ searchMessage }: EmptyCompanySearchProps) => {
  const { t } = useTranslation()
  const goToCompaniesAndLocations = useGoToPath(paths.companies)

  return (
    <EmptyCompanySearchContainer>
      <SearchIcon size="button" />
      <Column gap="4px">
        <Typography bold variant="p1Body">
          {t('noResultsFound')}
        </Typography>
        <Typography color={ThemeColor.b50} variant="p1Body">
          {searchMessage}
        </Typography>
      </Column>
      <ButtonLink onClick={goToCompaniesAndLocations}>
        <Row alignItems="center">
          <Typography bold color={ThemeColor.electric500} variant="p1Body">
            {t('createCompany')}
          </Typography>
          <ArrowRight size="message" />
        </Row>
      </ButtonLink>
    </EmptyCompanySearchContainer>
  )
}
